import {
  AutoComplete,
  Button,
  Col,
  DatePicker,
  Form,
  Select,
  Input,
  InputNumber,
  notification,
  Row,
  Table,
  Tag,
  Popover,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { MapPin, SquareCheck, CircleX } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ApiService from 'src/services/api-service';
import queryString from 'query-string';
import TokenService from 'src/services/token';
import dayjs from 'dayjs';
import { formatCurrency, removeAccentCharacters } from 'src/utils/helpers';
import useCustomers from 'src/hooks/use-customers';
import { CustomerInfo } from 'src/types/common.types';
import useSales from 'src/hooks/use-sales';
import { SaleInfo } from 'src/types/common.types';
import useWorkers from 'src/hooks/use-workers';
import { WorkerInfo } from 'src/types/common.types';
import useProducts from 'src/hooks/use-products';
import { ProductInfo } from 'src/types/common.types';
import { debounce } from 'lodash';

export default function OrderListingPage() {
  const user = TokenService.getUser();
  const [searchKeyword, setSearchKeyword] = useState('');
  const { data: customers, error: errorCustomers, isLoading: fetchCustomerLoading } = useCustomers(searchKeyword);
  const { data: sales, error: errorSales, isLoading: fetchSaleLoading } = useSales();
  const { data: workers, error: errorWorkers, isLoading: fetchWorkerLoading } = useWorkers();
  const { data: products, error: errorProducts, isLoading: fetchProductLoading } = useProducts();
  const location = useLocation();
  const navigate = useNavigate();
  const search = useMemo(() => {
    return queryString.parse(location.search, {
      arrayFormat: 'comma',
    });
  }, [location]);
  const FILTERS_DEFAULT = {
    page: 1,
    pageSize: search?.pageSize || 10,
    keyword: search?.keyword || '',
  };
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<any>(FILTERS_DEFAULT);
  const [totalOrder, setTotalOrder] = useState(0);
  const [exportDateRange, setExportDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);

  const [filterForm] = Form.useForm();

  const getOrders = async (filters: any) => {
    const params = { ...filters };
    if (params.orderDateFrom != null) {
      params.orderDateFrom = dayjs.isDayjs(params.orderDateFrom)
        ? params.orderDateFrom.format('YYYY-MM-DD')
        : params.orderDateFrom;
    }
    if (params.orderDateTo != null) {
      params.orderDateTo = dayjs.isDayjs(params.orderDateTo)
        ? params.orderDateTo.format('YYYY-MM-DD')
        : params.orderDateTo;
    }
    try {
      setLoading(true);
      const response = await ApiService.get('/api/orders', { params });
      setOrders(response.data.rows);
      setTotalOrder(response.data.count);
    } catch (error: any) {
      notification.error({
        message: 'Lỗi',
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const onSelectItem = (item: any) => {
    navigate(`/orders/${item.id}`);
  };

  const onSearchProduct = (keyword: string, option: any) => {
    return (
      option.props.searchname.includes(removeAccentCharacters(keyword).toLowerCase().trim()) ||
      option.props.code.includes(removeAccentCharacters(keyword).toLowerCase().trim())
    );
  };

  const onSearchCustomer = useCallback(
    debounce((value: string) => {
      setSearchKeyword(value);
    }, 500),
    [],
  );

  const onSearchSale = (keyword: string, option: any) => {
    return option.props.searchname.includes(removeAccentCharacters(keyword).toLowerCase().trim());
  };

  const onSearchWorker = (keyword: string, option: any) => {
    return option.props.searchname.includes(removeAccentCharacters(keyword).toLowerCase().trim());
  };

  const onTableChange = (pagination: any, tableFilters: any, sorter: any) => {
    const formValues = filterForm.getFieldsValue();
    const filterDates = {
      orderDateFrom: formValues.orderDate?.[0] ? (formValues.orderDate[0] as dayjs.Dayjs).format('YYYY-MM-DD') : null,
      orderDateTo: formValues.orderDate?.[1] ? (formValues.orderDate[1] as dayjs.Dayjs).format('YYYY-MM-DD') : null,
    };
    const newFilters = {
      ...filters,
      ...tableFilters,
      ...filterDates,
      page: pagination.current,
      pageSize: pagination.pageSize,
    };
    filters.page = pagination.current;
    filters.pageSize = pagination.pageSize;

    changeUrlQueryString(newFilters);
  };

  const onChangeFormFilter = () => {
    const formValues = filterForm.getFieldsValue();
    const filterDates = {
      orderDateFrom: formValues.orderDate?.[0] ? (formValues.orderDate[0] as dayjs.Dayjs).format('YYYY-MM-DD') : null,
      orderDateTo: formValues.orderDate?.[1] ? (formValues.orderDate[1] as dayjs.Dayjs).format('YYYY-MM-DD') : null,
    };
    delete formValues.orderDate;
    const newFilters = {
      ...filters,
      ...formValues,
      ...filterDates,
      page: 1,
    };
    debounceUpdateFilters(newFilters);
  };

  const onClearFilter = () => {
    filterForm.resetFields();
    filterForm.setFieldsValue({
      orderDateFrom: null,
      orderDateTo: null,
      orderDate: [null, null],
    });
    debounceUpdateFilters({ ...FILTERS_DEFAULT, keyword: null });
  };

  const debounceUpdateFilters = useCallback(
    debounce((filters: any) => {
      changeUrlQueryString(filters);
    }, 500),
    [],
  );

  const changeUrlQueryString = (filters: any) => {
    navigate(
      '/orders?' + queryString.stringify(filters, { arrayFormat: 'comma', skipEmptyString: true, skipNull: true }),
    );
  };

  const onExportCSV = async (dateFrom: string, dateTo: string) => {
    try {
      const response = await ApiService.post('/api/orders/export_csv', {
        from: dateFrom,
        to: dateTo,
      });
      const csvBlob = new Blob([response.data], { type: 'text/csv' });
      const csvUrl = window.URL.createObjectURL(csvBlob);

      const link = document.createElement('a');
      link.href = csvUrl;

      link.setAttribute(
        'download',
        `Danh sách đơn hàng_${dayjs(dateFrom).format('DD-MM-YYYY')}_${dayjs(dateTo).format('DD-MM-YYYY')}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(csvUrl);
    } catch (error: any) {
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Có lỗi xảy ra khi xuất file',
      });
    }
  };

  useEffect(() => {
    getOrders(filters);
  }, [filters]);
  useEffect(() => {
    const refinedFilters: any = {};
    console.log('search', search);
    Object.keys(search).forEach(key => {
      if (['productIds', 'saleIds', 'workerIds', 'customerIds', 'invoiceIssued'].includes(key) && search[key]) {
        if (Array.isArray(search[key])) {
          refinedFilters[key] = Object.values(search[key]!).map(x => +x!);
        } else {
          refinedFilters[key] = [+search[key]!];
        }
      } else if (key.indexOf('From') > 1 || key.indexOf('To') > 1) {
        try {
          refinedFilters[key] = dayjs(search[key] as string, 'YYYY-MM-DD');
        } catch (error) {
          console.log('ERROR', error);
        }
      } else {
        if (Array.isArray(search[key]))
          refinedFilters[key] = Object.values(search[key]!).map(x => (!isNaN(+x!) ? +x! : x));
        else {
          refinedFilters[key] = search[key];
        }
      }
      setFilters(refinedFilters);
      filterForm.setFieldsValue({
        ...refinedFilters,
        orderDateFrom: null,
        orderDateTo: null,
        orderDate: [refinedFilters.orderDateFrom, refinedFilters.orderDateTo],
      });
    });
  }, [search]);

  return (
    <div className="md:p-8 sm:p-4 sm:pt-12">
      <Row gutter={[0, 32]} className="pb-6">
        <Link to={'/orders/entry'} className="mr-4">
          <Button size="large" type="primary" icon={<PlusOutlined />}>
            Đơn hàng mới
          </Button>
        </Link>
        <Popover
          title="Chọn khoảng thời gian xuất file"
          placement="bottomRight"
          content={
            <div className="flex flex-col gap-3" style={{ width: 300 }}>
              <DatePicker.RangePicker
                format={'DD-MM-YYYY'}
                minDate={dayjs('2025-11-01', 'YYYY-MM-DD')}
                maxDate={dayjs()}
                placeholder={['Từ ngày', 'Đến ngày']}
                className="w-full"
                value={exportDateRange as any}
                onChange={dates => {
                  setExportDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null]);
                }}
                disabledDate={current => {
                  // If start date is selected, disable dates that would make range > 30 days (inclusive)
                  // diff returns days between dates, so diff of 30 means 31 days total (inclusive)
                  if (exportDateRange[0] && !exportDateRange[1]) {
                    const daysDiff = Math.abs(current.diff(exportDateRange[0], 'day'));
                    return daysDiff >= 30;
                  }
                  // If end date is selected but start is not (shouldn't happen, but handle it)
                  if (exportDateRange[1] && !exportDateRange[0]) {
                    const daysDiff = Math.abs(current.diff(exportDateRange[1], 'day'));
                    return daysDiff >= 30;
                  }
                  return false;
                }}
              />
              <Button
                type="primary"
                onClick={() => {
                  if (!exportDateRange[0] || !exportDateRange[1]) {
                    notification.warning({
                      message: 'Cảnh báo',
                      description: 'Vui lòng chọn đầy đủ khoảng thời gian',
                    });
                    return;
                  }
                  const daysDiff = Math.abs(exportDateRange[1].diff(exportDateRange[0], 'day'));
                  // diff of 30 means 31 days total (inclusive), so we check >= 30
                  if (daysDiff >= 31) {
                    notification.warning({
                      message: 'Cảnh báo',
                      description: 'Khoảng thời gian không được vượt quá 30 ngày',
                    });
                    return;
                  }
                  onExportCSV(exportDateRange[0].format('YYYY-MM-DD'), exportDateRange[1].format('YYYY-MM-DD'));
                }}
                disabled={!exportDateRange[0] || !exportDateRange[1]}
              >
                Xuất file
              </Button>
            </div>
          }
          trigger="click"
        >
          <Button type="dashed" size="large">
            Xuất file
          </Button>
        </Popover>
      </Row>
      <Form form={filterForm} layout="horizontal" labelCol={{ span: 0 }} wrapperCol={{ span: 24 }}>
        <Row gutter={[16, 0]} className="pb-6">
          <Col span={6}>
            <Form.Item name="orderDate" label="Ngày lắp">
              <DatePicker.RangePicker
                format={'DD-MM-YYYY'}
                maxDate={dayjs()}
                placeholder={['Ngày lắp từ', 'đến']}
                className="w-full"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="customerIds" label="Mã khách hàng">
              <Select
                mode="multiple"
                options={customers?.map((customer: CustomerInfo) => ({
                  value: customer.customerId,
                  label: customer.name,
                }))}
                filterOption={false}
                showSearch={true}
                placeholder="Chọn khách hàng"
                onSearch={onSearchCustomer}
                // notFoundContent={fetchCustomerLoading ? <span>Đang tải...</span> : <span>Không tìm thấy kết quả</span>}
                // onSelect={(value: number) => {
                //   console.log(value);
                //   filterForm.setFieldsValue({
                //     customerIds: value,
                //   });
                // }}
              >
                <Input.Search />
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="orderId" label="Mã đơn hàng">
              <InputNumber placeholder="Nhập mã đơn hàng" controls={false} className="w-full" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="saleIds" label="Mã sale">
              <Select
                mode="multiple"
                options={sales?.map((sale: SaleInfo) => ({
                  value: sale.id,
                  label: sale.fullName,
                  searchname: removeAccentCharacters(sale.fullName).toLowerCase().trim(),
                }))}
                showSearch={true}
                placeholder="Chọn sale"
                filterOption={onSearchSale}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="workerIds" label="Mã thợ">
              <Select
                mode="multiple"
                options={workers?.map((worker: WorkerInfo) => ({
                  value: worker.id,
                  label: worker.name,
                  searchname: removeAccentCharacters(worker.name).toLowerCase().trim(),
                }))}
                placeholder="Chọn thợ"
                showSearch={true}
                filterOption={onSearchWorker}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="productIds" label="Mã sản phẩm">
              <Select
                mode="multiple"
                options={products?.map((product: ProductInfo) => ({
                  value: product.id,
                  label: product.name,
                  code: product.code,
                  searchname: removeAccentCharacters(product.name).toLowerCase().trim(),
                }))}
                placeholder="Chọn sản phẩm"
                showSearch={true}
                filterOption={onSearchProduct}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="invoiceIssued" label="Trạng thái hóa đơn">
              <Select
                mode="multiple"
                options={[
                  { value: 0, label: 'Chưa xuất' },
                  { value: 1, label: 'Đã xuất' },
                  { value: 2, label: 'Đã xuất & điều chỉnh' },
                ]}
                placeholder="Chọn trạng thái"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="taxCode" label="Mã số thuế">
              <Input placeholder="Nhập mã số thuế" maxLength={20} allowClear />
            </Form.Item>
          </Col>
          <Col md={6} sm={6} xs={6}>
            <Form.Item noStyle className="mb-0 mr-2">
              <Button onClick={onChangeFormFilter} className="mr-2" loading={loading}>
                Lọc dữ liệu
              </Button>
            </Form.Item>
            <Form.Item noStyle className="mb-0">
              <Button onClick={onClearFilter} type="dashed" danger loading={loading}>
                Xóa lọc
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Table
        dataSource={orders}
        tableLayout="fixed"
        scroll={{ x: 'max-content' }}
        pagination={{ total: totalOrder, current: filters.page, showSizeChanger: true }}
        onChange={onTableChange}
        loading={loading}
        columns={[
          {
            title: 'Ngày lắp',
            key: 'orderDate',
            dataIndex: 'orderDate',
            render: (value: any, record: any, index: number) => {
              return <>{dayjs(value).format('DD-MM-YYYY')}</>;
            },
            width: 110,
            fixed: 'left',
          },
          {
            title: 'Mã đơn hàng',
            key: 'id',
            dataIndex: 'id',
            width: 90,
            fixed: 'left',
            render: (value: any, record: any, index: number) => {
              return (
                <Link
                  to={`/orders/${record.id}`}
                  className={`font-bold select-text ${record.status == 2 ? 'text-red-500 hover:text-red-600' : 'text-blue-500 hover:text-blue-600'}`}
                >
                  #{record.id}
                </Link>
              );
            },
          },
          {
            title: 'Loại đơn hàng',
            key: 'orderType',
            dataIndex: 'orderType',
            width: 120,
            render: (_: any, record: any, index: number) => {
              return (
                <>
                  {(() => {
                    switch (record.type) {
                      case 1:
                        return <Tag color="blue">Đơn hàng mới</Tag>;
                      case 2:
                        return <Tag color="green">Đơn hàng bảo hành</Tag>;
                      case 3:
                        return <Tag color="orange">Đơn cọc</Tag>;
                      default:
                        return <Tag>-</Tag>;
                    }
                  })()}
                </>
              );
            },
          },
          {
            title: 'Sale',
            key: 'sale',
            dataIndex: 'saleId',
            width: 150,
            render: (_: any, record: any, index: number) => {
              return <>{record.saleInfo?.fullName || '-'}</>;
            },
          },
          ...(![7].includes(user.role)
            ? [
                {
                  title: 'Thợ',
                  key: 'workers',
                  dataIndex: 'workers',
                  width: 250,
                  render: (_: any, record: any, index: number) => {
                    return <>{record.workers?.map((worker: any) => <p>{worker.name}</p>)}</>;
                  },
                },
              ]
            : []),
          {
            title: 'Sản phẩm',
            key: 'orderItems',
            dataIndex: 'orderItems',
            width: 150,
            render: (_: any, record: any, index: number) => {
              return (
                <>
                  {record.orderItems?.map((item: any) => (
                    <p>
                      {item.quantity} x <b>{item.productInfo?.code}</b>
                    </p>
                  ))}
                </>
              );
            },
          },
          {
            title: 'Khách hàng',
            key: 'customerInfo',
            width: 300,
            dataIndex: 'customerInfo',
            render: (_: any, record: any, index: number) => {
              const address = record.address || record.customerInfo?.address;
              return (
                <div className="line-clamp-3 min-w-0 break-words text-sm leading-snug">
                  <span className="font-bold">{record.customerInfo?.name}</span>
                  {address && (
                    <span className="mt-0.5 block">
                      <MapPin className="mr-1 inline-block align-text-bottom shrink-0" size={16} />
                      {address}
                    </span>
                  )}
                </div>
              );
            },
          },
          {
            title: 'Ghi chú',
            key: 'note',
            dataIndex: 'note',
            width: 200,
            render: (_: any, record: any, index: number) => {
              return <div className="line-clamp-3 min-w-0 break-words text-sm leading-snug">{record.note}</div>;
            },
          },
          {
            title: 'GTĐH trước thuế',
            key: 'amount',
            dataIndex: 'amount',
            render: (_: any, record: any, index: number) => {
              return <>{formatCurrency(record.amount - record.vatAmount)}</>;
            },
          },
          {
            title: 'Thuế GTGT',
            key: 'vatAmount',
            dataIndex: 'vatAmount',
            render: (_: any, record: any, index: number) => {
              return <>{formatCurrency(record.vatAmount)}</>;
            },
          },
          {
            title: [7].includes(user.role) ? 'GTĐH sau thuế' : 'Doanh thu',
            key: 'revenue',
            dataIndex: 'revenue',
            render: (_: any, record: any, index: number) => {
              return <>{formatCurrency((record.amount + record.vatAmount).toFixed(0))}</>;
            },
          },
          ...([1, 7].includes(user.role)
            ? [
                {
                  title: 'Thông tin hóa đơn',
                  key: 'invoiceInfo',
                  width: 300,
                  dataIndex: 'invoiceInfo',
                  render: (_: any, record: any, index: number) => {
                    return (
                      <>
                        {record.invoiceInfo ? (
                          <>
                            <p>
                              <b>Tên:</b> {record.invoiceInfo?.name}
                            </p>
                            <p>
                              <b>MST:</b> {record.invoiceInfo?.taxCode}
                            </p>
                            <p>
                              <b>Địa chỉ:</b> {record.invoiceInfo?.address}
                            </p>
                          </>
                        ) : (
                          '-'
                        )}
                      </>
                    );
                  },
                },
              ]
            : []),
          {
            title: 'Đã xuất',
            key: 'invoiceIssued',
            dataIndex: 'invoiceIssued',
            render: (_: any, record: any, index: number) => {
              const renderInvoiceIssued = () => {
                if (record.vat || record.invoiceInfo) {
                  if (record.invoiceIssued === 1) {
                    return <SquareCheck color="green" />;
                  }
                  if (record.invoiceIssued === 2) {
                    return <SquareCheck color="blue" />;
                  }
                  return <CircleX color="red" />;
                }
                return '-';
              };
              return <div className="flex items-center justify-center">{renderInvoiceIssued()}</div>;
            },
          },
        ]}
      ></Table>
    </div>
  );
}
